import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

const ALLOWED_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'text/plain', 'text/html', 'text/markdown', 'text/css', 'text/csv',
  'text/xml', 'text/javascript',
  'application/json', 'application/javascript', 'application/xml',
  'application/x-yaml', 'application/yaml',
  'image/svg+xml',
]);

// Extensions that are text-based but browsers may report empty or generic MIME types for
const TEXT_EXTENSIONS = new Set([
  '.txt', '.html', '.htm', '.md', '.markdown', '.css', '.csv', '.xml',
  '.json', '.js', '.mjs', '.cjs', '.ts', '.jsx', '.tsx',
  '.py', '.yaml', '.yml', '.svg', '.sh', '.bash', '.zsh',
  '.sql', '.env', '.cfg', '.ini', '.toml', '.log', '.rst', '.tex',
  '.rb', '.go', '.rs', '.java', '.c', '.cpp', '.h', '.hpp',
  '.php', '.swift', '.kt', '.lua', '.r', '.pl', '.ex', '.exs',
  '.vue', '.svelte', '.astro', '.graphql', '.gql', '.prisma',
]);
const MAX_FILE_SIZE = 32 * 1024 * 1024; // 32MB
const MAX_FILES = 5;

export default function useFileUpload() {
  const [files, setFiles] = useState([]);

  const uploadFile = useCallback(async (file) => {
    const ext = (file.name || '').includes('.')
      ? '.' + file.name.split('.').pop().toLowerCase()
      : '';
    const isAllowedByType = ALLOWED_TYPES.has(file.type);
    const isAllowedByExt = TEXT_EXTENSIONS.has(ext);
    if (!isAllowedByType && !isAllowedByExt) {
      return { error: `Unsupported file type: ${file.type || ext}` };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { error: 'File too large. Maximum size is 32MB.' };
    }

    // Add placeholder
    const tempId = Math.random().toString(36).substring(2);
    const placeholder = { tempId, filename: file.name, mime_type: file.type, size_bytes: file.size, uploading: true, error: null, file_id: null };
    setFiles(prev => {
      if (prev.length >= MAX_FILES) return prev;
      return [...prev, placeholder];
    });

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE_URL}/chat/upload`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        setFiles(prev => prev.map(f => f.tempId === tempId ? { ...f, uploading: false, error: err.detail || 'Upload failed' } : f));
        return { error: err.detail };
      }

      const data = await res.json();
      setFiles(prev => prev.map(f => f.tempId === tempId ? { ...f, ...data, uploading: false } : f));
      return { data };
    } catch (err) {
      setFiles(prev => prev.map(f => f.tempId === tempId ? { ...f, uploading: false, error: err.message } : f));
      return { error: err.message };
    }
  }, []);

  const removeFile = useCallback((tempId) => {
    setFiles(prev => prev.filter(f => f.tempId !== tempId));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const isUploading = files.some(f => f.uploading);
  const readyFiles = files.filter(f => f.file_id && !f.uploading && !f.error);

  return { files, isUploading, readyFiles, uploadFile, removeFile, clearFiles };
}
